import { AddBookDialog } from '@/components/admin/add-book-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { trpc } from '@/lib/trpc';
import { useState } from 'react';
import { toast } from 'sonner';

// Admin / Books — manage the bookmakers tracked by the scanner.
//
// Endpoints:
//   - config.get             — list current state
//   - config.addBook         — modal
//   - config.removeBook      — row action; idempotent (server returns same
//                              state if book absent)
//   - config.setBookMetadata — inline type select (sharp / soft)

interface BookMeta {
  type?: unknown;
  notes?: unknown;
  [k: string]: unknown;
}

function metaType(meta: BookMeta | undefined): 'sharp' | 'soft' | 'unknown' {
  if (meta?.type === 'sharp') return 'sharp';
  if (meta?.type === 'soft') return 'soft';
  return 'unknown';
}

export function AdminBooksPage() {
  const utils = trpc.useUtils();
  const cfg = trpc.config.get.useQuery();
  const [adding, setAdding] = useState(false);

  const remove = trpc.config.removeBook.useMutation({
    onSuccess: async () => {
      toast.success('Book removido');
      await utils.config.get.invalidate();
    },
    onError: (err) => toast.error('No se pudo remover', { description: err.message }),
  });

  const setMeta = trpc.config.setBookMetadata.useMutation({
    onSuccess: async () => {
      toast.success('Metadata actualizada');
      await utils.config.get.invalidate();
    },
    onError: (err) => toast.error('No se pudo actualizar', { description: err.message }),
  });

  const books = cfg.data?.allowedBooks ?? [];
  const priorities = cfg.data?.priorityBooks ?? {};
  const metadata = (cfg.data?.bookMetadata ?? {}) as Record<string, BookMeta>;

  return (
    <div className="space-y-4">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="font-brand text-3xl tracking-[0.25em] text-foreground">
            CASAS DE APUESTAS
          </h1>
          <p className="text-xs text-muted-foreground">
            Casas que el scanner monitorea. <b>Sharp</b> entra al consenso de mercado;
            <b> soft</b> aparece como cuota disponible pero no mueve el consenso.
          </p>
        </div>
        <Button onClick={() => setAdding(true)}>+ Agregar casa</Button>
      </header>

      {cfg.isError && (
        <Alert variant="destructive">
          <AlertTitle>Error cargando config</AlertTitle>
          <AlertDescription>{cfg.error.message}</AlertDescription>
        </Alert>
      )}
      {cfg.isLoading && <Skeleton className="h-64" />}

      {cfg.data && (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Slug</TableHead>
                <TableHead>Prioridad</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Notas</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {books.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                    Sin books configurados. Agregá el primero arriba.
                  </TableCell>
                </TableRow>
              )}
              {books.map((slug) => {
                const meta = metadata[slug];
                const t = metaType(meta);
                const notes = typeof meta?.notes === 'string' ? meta.notes : '';
                return (
                  <TableRow key={slug}>
                    <TableCell className="font-mono text-foreground">{slug}</TableCell>
                    <TableCell className="font-mono">{priorities[slug] ?? '—'}</TableCell>
                    <TableCell>
                      <Select
                        value={t === 'unknown' ? 'soft' : t}
                        onValueChange={(v) => {
                          setMeta.mutate({
                            book: slug,
                            metadata: { ...meta, type: v },
                          });
                        }}
                      >
                        <SelectTrigger className="h-7 w-[100px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sharp">sharp</SelectItem>
                          <SelectItem value="soft">soft</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {notes ? (
                        <span className="text-sm text-muted-foreground">{notes}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                      {t === 'sharp' && (
                        <Badge variant="outline" className="ml-2 border-positive/40 text-positive">
                          consensus
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (confirm(`¿Quitar ${slug} del scanner?`)) {
                            remove.mutate({ book: slug });
                          }
                        }}
                      >
                        Remover
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <AddBookDialog open={adding} onClose={() => setAdding(false)} existingBooks={books} />
    </div>
  );
}
